define(function(require, exports, module) {
    'use strict';

    var _ = require('underscore');
    var domUtil = require('util/dom.util');
    var Form = require('class/form.class');

    var ruleUtil = {

        /**
         * Find minimum number of rule recalculations we need to perform to ensure all chained rules apply correctly
         * For example, Country -> Province -> City
         * @param rules
         * @return {number}
         */
        getMaxRuleChainLength: function(rules) {
            var emitters = {};
            var targets = {};

            rules.forEach(function(rule) {
                rule.conditions.forEach(function(condition) {
                    emitters[condition.field] = 1;
                });

                rule.actions.forEach(function(action) {
                    targets[action.field] = 1;
                });
            });

            // check if there are fields, used in conditions, that could be hidden by rules
            var junctions = _.intersection(_.keys(emitters), _.keys(targets));
            if (!junctions.length) {
                return 1;
            }

            var findRules = function(fieldName) {
                return rules.filter(function(rule) {
                    return rule.conditions.some(function(condition) {
                        return condition.field === fieldName;
                    });
                });
            };

            var findMaxChainLength = function(parentLength, targetFieldNames, chain) {
                return targetFieldNames.reduce(function(maxLengthFromParent, targetFieldName) {
                    // prevent circular references
                    if (chain.includes(targetFieldName)) {
                        return maxLengthFromParent;
                    }

                    // find current target in other rules' conditions
                    var formRules = findRules(targetFieldName);
                    if (!formRules.length) {
                        return maxLengthFromParent;
                    }

                    var nextChain = chain.concat(targetFieldName);
                    // find max chain length for current targetFieldName
                    return formRules.reduce(function(maxLengthForTarget, formRule) {
                        var nextChainTargets = _.map(formRule.actions, 'field');
                        return Math.max(maxLengthForTarget,
                            // find max chain length for current rule
                            // increase length only on going deep into recursion
                            findMaxChainLength(parentLength + 1, nextChainTargets, nextChain));
                    }, maxLengthFromParent);
                }, parentLength);
            };

            return findMaxChainLength(1, junctions, []);
        },

        applyFormRules: function(view, rules) {
            if (!rules || !rules.length) {
                return;
            }

            var runCount = view.options.config.genericRuleChainLength;
            var formRawData = ruleUtil._getRawRegData(view);
            var fieldsVisibilityMap = formRawData.visibilityMap;

            for (var i = 0; i < runCount; i++) {
                var partialMap = ruleUtil._checkRules(rules, formRawData.data, fieldsVisibilityMap);
                _.extend(fieldsVisibilityMap, partialMap);
            }

            ruleUtil._applyRules(view, fieldsVisibilityMap);
        },

        _getRawRegData: function(view) {
            var forms = view.childViews.map(function(stepEl) {
                return stepEl.form;
            });

            var data = {};
            var visibilityMap = {};
            forms.forEach(function(form) {
                var rawData = ruleUtil._getRawFormData(form);
                _.extend(data, rawData.data);
                _.extend(visibilityMap, rawData.visibilityMap);
            });

            return {
                data: data,
                visibilityMap: visibilityMap
            };
        },

        _getRawFormData: function(form) {
            var data = {};
            var visibilityMap = {};

            var fields = form.$el.find('fieldset');
            _.each(fields, function(field) {
                var fieldName = this._getFieldName(field);
                if (fieldName) {
                    visibilityMap[fieldName] = !field.classList.contains('hidden');
                }
            }.bind(this));
            var buttons = form.$el.find('button');
            _.each(buttons, function(button) {
                var btnName = button.dataset.name;
                if (btnName) {
                    visibilityMap[btnName] = !button.classList.contains('hidden');
                }
            });

            var getRadioButtonId = function(el) {
                if (el.nodeName === 'FIELDSET') {
                    return this._getFieldName(el);
                } else if (el.nodeName === 'BODY') {
                    return '';
                }

                return getRadioButtonId(el.parentElement);
            }.bind(this);

            _.chain(form.el.elements)
                .filter('id')
                .filter(function(el) {
                    if (el.nodeName.toLowerCase() === 'fieldset') {
                        return false;
                    }

                    var type = el.type;
                    if (type === 'submit' || type === 'reset' || type === 'button') {
                        return false;
                    }

                    if (type === 'radio' && !el.checked) {
                        return false;
                    }

                    return true;
                })
                .each(function(el) {
                    var id = el.id;
                    var value;

                    if (el.type === 'checkbox') {
                        value = el.checked;
                    } else if (el.type === 'radio') {
                        id = getRadioButtonId(el);
                        value = el.value;
                    } else {
                        value = domUtil.getInputValue(el);
                    }

                    data[id] = value;
                });

            return {
                data: data,
                visibilityMap: visibilityMap
            };
        },

        _getFieldName: function(el) {
            var nameTokenRe = /(?:fieldset_name_|field_name_)(\S+)\b/;
            var nameMatch = el.className.match(nameTokenRe);
            return nameMatch[1] || el.className;
        },

        _checkRules: function(formRules, formData, visibilityMap) {
            var fieldsVisibilityMap = {};

            formRules.forEach(function(rule) {
                // apply conditions by AND
                var result = rule.conditions.every(ruleUtil._checkCondition.bind(null, formData, visibilityMap));

                rule.actions.forEach(function(action) {
                    // apply rules by OR
                    fieldsVisibilityMap[action.field] = fieldsVisibilityMap[action.field] || result;
                });
            });

            return fieldsVisibilityMap;
        },

        _checkCondition: function(formData, visibilityMap, condition) {
            if (!visibilityMap[condition.field]) {
                return false;
            }

            var operator = condition.operator;
            var fieldValue = formData[condition.field];

            switch (operator) {
                case 'ComparisonOperator.Equal':
                    return fieldValue === condition.value;
                case 'ComparisonOperator.NotEqual':
                    return fieldValue != null && fieldValue !== '' && fieldValue !== condition.value;
                // case 'ComparisonOperator.IsEmpty':
                //     return !fieldValue;
                // case 'ComparisonOperator.IsNotEmpty':
                //     return Boolean(fieldValue);
                default:
                    return false;
            }
        },

        _applyRules: function(view, fieldsVisibilityMap) {
            _.each(fieldsVisibilityMap, function(show, fieldName) {
                var button = view.$el.find('button[data-name="' + fieldName + '"]');
                if (button.length > 0) {
                    button.toggleClass('hidden', !show);
                } else {
                    Form.toggleFieldVisibility(fieldName, show, view.el);
                }
            });
        }
    };

    module.exports = ruleUtil;
});
